// Pure shaping for the scorecard results modal: one scorecard on one
// applicant, every evaluator's submission side by side.

import scoreCardTemplatesMessages from 'containers/ScoreCardTemplates/messages';
import globalMessages from 'shared/global/messages';

import { messages as formMessages } from '../ScorecardForm/messages';
import {
  AnsweredQuestion,
  ScorecardAnswer,
  ScorecardQuestion,
  ScorecardQuestionType,
} from '../ScorecardForm/scorecardFillForm';

export type RecommendationKey = 'strong_no' | 'no' | 'yes' | 'strong_yes';

export interface ScorecardEvaluation {
  // The evaluator's external id.
  user: { id: string | null; name: string | null };
  current_user: boolean; // eslint-disable-line camelcase
  // `pending` covers an evaluator with a draft — their answers never arrive.
  status: 'submitted' | 'pending';
  submitted_at: string | null; // eslint-disable-line camelcase
  invited_at: string | null; // eslint-disable-line camelcase
  // The mode the evaluator submitted under, from the answer snapshots; a
  // template retyped later must not restyle a historical submission.
  submitted_template_type: 'unweighted' | 'weighted' | 'text' | null; // eslint-disable-line camelcase
  score: number | null;
  recommendation: RecommendationKey | null;
  answers: ScorecardAnswer[];
}

export interface ScorecardResultsData {
  template: {
    id: number;
    name: string;
    template_type: 'unweighted' | 'weighted' | 'text'; // eslint-disable-line camelcase
    questions: ScorecardQuestion[];
  };
  applicant: { id: string; name: string };
  evaluations: ScorecardEvaluation[];
}

export const QUESTION_TYPE_LABELS = {
  rating: scoreCardTemplatesMessages.typeRating,
  yes_no: scoreCardTemplatesMessages.typeYesNo, // eslint-disable-line camelcase
  dropdown: scoreCardTemplatesMessages.typeDropdown,
  free_text: scoreCardTemplatesMessages.typeFreeText, // eslint-disable-line camelcase
  recommendation: scoreCardTemplatesMessages.typeRecommendation,
} as const;

export const RECOMMENDATION_LABELS = {
  strong_no: formMessages.recommendationStrongNo, // eslint-disable-line camelcase
  no: globalMessages.no,
  yes: globalMessages.yes,
  strong_yes: formMessages.recommendationStrongYes, // eslint-disable-line camelcase
} as const;

export const isPositiveRecommendation = (key: RecommendationKey): boolean =>
  key === 'yes' || key === 'strong_yes';

// One comparison row per question, excluding the recommendation (it gets its
// own summary row under the scores).
export interface ComparisonRow {
  // String(question_id), or a snapshot signature once the question is
  // deleted — deletion nullifies question_id on the released answers.
  key: string;
  text: string;
  question_type: ScorecardQuestionType; // eslint-disable-line camelcase
}

const answerRowKey = (answer: ScorecardAnswer): string => {
  if (answer.question_id !== null && answer.question_id !== undefined) {
    return String(answer.question_id);
  }
  // The snapshot id: stable across wording edits, unique across questions,
  // so evaluators who answered different versions still share one row.
  if (answer.question?.id !== null && answer.question?.id !== undefined) {
    return `deleted:${answer.question.id}`;
  }
  // Pre-id snapshots fall back to a content signature.
  return `deleted:${answer.question?.position ?? ''}:${
    answer.question?.question_type ?? ''
  }:${answer.question?.text ?? ''}`;
};

// The comparison rows, worded as the evaluators answered them. Snapshot
// first: a question edited after a submission keeps the wording that
// submission answered (the latest submission wins when versions differ), an
// unanswered question reads from the live template, and an answered question
// since deleted from the template still gets a row at its snapshotted
// position.
export const comparisonRows = (data: ScorecardResultsData): ComparisonRow[] => {
  const answered = new Map<string, AnsweredQuestion>();
  [...data.evaluations]
    .filter(evaluation => evaluation.status === 'submitted')
    .sort((a, b) => (a.submitted_at ?? '').localeCompare(b.submitted_at ?? ''))
    .forEach(evaluation =>
      evaluation.answers.forEach(answer => {
        if (answer.question?.text && answer.question.question_type) {
          answered.set(answerRowKey(answer), answer.question);
        }
      }),
    );

  const rows: ComparisonRow[] = data.template.questions.map(question => {
    const snapshot = answered.get(String(question.id));
    return {
      key: String(question.id),
      text: snapshot?.text ?? question.text,
      question_type: snapshot?.question_type ?? question.question_type,
    };
  });

  const templateKeys = new Set(rows.map(row => row.key));
  [...answered.entries()]
    .filter(([key]) => !templateKeys.has(key))
    .map(([key, question]) => ({
      key,
      text: question.text as string,
      question_type: question.question_type as ScorecardQuestionType,
      position: question.position ?? rows.length,
    }))
    .sort((a, b) => a.position - b.position)
    .forEach(({ position, ...row }) =>
      rows.splice(Math.min(position, rows.length), 0, row),
    );

  return rows.filter(row => row.question_type !== 'recommendation');
};

export const answersByRowKey = (
  evaluation: ScorecardEvaluation,
): Map<string, ScorecardAnswer> =>
  new Map(evaluation.answers.map(answer => [answerRowKey(answer), answer]));

// What one cell shows. `value` is the submitted answer; `note` is the
// evaluator's comment under it. A free-text answer reads as a note, and an
// answer holding only a note still shows it.
export type AnswerDisplay =
  | { kind: 'none' }
  | { kind: 'value'; value: string; note: string | null }
  | { kind: 'yesNo'; value: 'yes' | 'no'; note: string | null }
  | { kind: 'note'; note: string };

const noteOrNone = (note: string | null): AnswerDisplay =>
  note ? { kind: 'note', note } : { kind: 'none' };

export const answerDisplay = (
  question: ComparisonRow,
  answer: ScorecardAnswer | undefined,
): AnswerDisplay => {
  if (!answer) return { kind: 'none' };
  const note = answer.note?.trim() ? answer.note.trim() : null;

  // The answer's own snapshot type: a question retyped after submission must
  // not reinterpret what was saved.
  switch (answer.question?.question_type ?? question.question_type) {
    case 'rating':
      return answer.score === null || answer.score === undefined
        ? noteOrNone(note)
        : { kind: 'value', value: String(answer.score), note };
    case 'yes_no':
      return answer.value === 'yes' || answer.value === 'no'
        ? { kind: 'yesNo', value: answer.value, note }
        : noteOrNone(note);
    case 'free_text':
      return answer.value?.trim()
        ? { kind: 'note', note: answer.value.trim() }
        : { kind: 'none' };
    default:
      return answer.value?.trim()
        ? { kind: 'value', value: answer.value, note }
        : noteOrNone(note);
  }
};

// Which score row the summary shows. The live template decides while it can;
// once it is comments-only, the submissions' own modes decide, so a retyped
// template neither hides a stored score nor invents a row with none to show.
export const scoreRowMode = (
  data: ScorecardResultsData,
): 'unweighted' | 'weighted' | null => {
  if (data.template.template_type !== 'text')
    return data.template.template_type;

  const submittedMode = data.evaluations
    .map(evaluation => evaluation.submitted_template_type)
    .find(mode => mode === 'unweighted' || mode === 'weighted');
  return submittedMode ?? null;
};

// A note longer than this renders clamped behind a Show more toggle.
export const NOTE_PREVIEW_LENGTH = 90;

export const notePreview = (note: string): string =>
  `${note.slice(0, NOTE_PREVIEW_LENGTH).trim()}…`;
